import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import PropTypes from "prop-types";
import { useState, useEffect } from "react";

import ChooseFormulaModal from "@/components/membership/ChooseFormulaModal/ChooseFormulaModal";
import CrossProductTabs from "@/components/membership/Template/CrossProductTabs";
import TemplateInput from "@/components/membership/Template/TemplateInput";
import { useSidebarContext } from "@/contexts/SidebarContext";
import { useUpdateDataContext } from "@/contexts/UpdateDataContext";
import useAdaFieldsHandler from "@/hooks/useAdaFieldsHandler";
import useGetCachesHandler from "@/hooks/useGetCachesHandler";
import TableStyles from "@/styles/TableStyles";

import "@/components/membership/Template/TemplateEditorStyles.css";

const TemplateMapper = ({ handleUpdatedTemplate, initialTemplateData }) => {
  const { getAdaFields, handleMultipleTemplateChanges } = useAdaFieldsHandler();
  const { getFormulaeCache } = useGetCachesHandler();
  const { path } = useSidebarContext();
  const { workflow } = useUpdateDataContext();
  const [template, setTemplate] = useState(null);
  const [prefix, setPrefix] = useState("");
  const [formulae, setFormulae] = useState({});
  const [adaFields, setAdaFields] = useState([]);
  const isPoliticalData = workflow === "political";

  useEffect(() => {
    if (path.length === 4) {
      setPrefix(path[3]);
    } else {
      setPrefix("");
    }
  }, [path]);

  useEffect(() => {
    setTemplate(initialTemplateData);
    getFormulaeCache().then((data) => {
      setFormulae(data);
    });
    // getFormulaeCache is recreated by useGetCachesHandler every render;
    // intentionally react to initialTemplateData only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTemplateData]);

  const rowNameLabel = (row) => {
    const symbol = row.priority ? "+" : row.required ? "*" : "";
    return `${prefix} ${row.name} ${symbol}`;
  };

  useEffect(() => {
    // callback to templateEditor
    if (template) {
      handleUpdatedTemplate(template);
    }
    // handleUpdatedTemplate is an inline function from the parent;
    // depending on it would cause infinite re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  useEffect(() => {
    if (template) {
      const updateTemplateFields = async () => {
        const newAdaFields = await getAdaFields(template);
        setAdaFields(newAdaFields.fields);
        const templateWithNewFields = { ...template };
        // every time the path changes, re-assign these new fields to the path[0] node of the template
        templateWithNewFields[path[0]][newAdaFields.subCategoryName] =
          newAdaFields.fields;
        setTemplate(templateWithNewFields);
      };
      updateTemplateFields();
    }
    // getAdaFields is recreated by useAdaFieldsHandler every render;
    // intentionally react to path only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  const saveFormula = (rowIndex, obj) => {
    setTemplate((prevTemplate) => {
      return handleMultipleTemplateChanges({ ...prevTemplate }, rowIndex, obj);
    });
  };

  const cleanAffiliateColumns = (columnValues) => {
    columnValues = columnValues.filter((item) => item !== null);
    columnValues = columnValues.length ? columnValues : null;
    return columnValues;
  };

  const handleInputChange = (newAffiliateColumns, rowIndex) => {
    const affiliateColumns = cleanAffiliateColumns(newAffiliateColumns);
    setTemplate((prevTemplate) => {
      return handleMultipleTemplateChanges({ ...prevTemplate }, rowIndex, {
        affiliate_columns: affiliateColumns,
      });
    });
  };

  return (
    <>
      {template && (
        <TableContainer
          component={Paper}
          className="template-editor-mappingContainer"
        >
          <CrossProductTabs sx={{ width: "100%" }} />
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell sx={TableStyles.THFont}>ADA Name</TableCell>
                <TableCell sx={TableStyles.THFont} align="center">
                  Affiliate Columns
                </TableCell>
                {!isPoliticalData && (
                  <>
                    <TableCell sx={TableStyles.THFont} align="right">
                      Formula
                    </TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody data-testid="template-mapper-table-body">
              {adaFields.length > 0 &&
                adaFields.map(
                  (row, index) =>
                    row.name && (
                      <TableRow
                        data-testid={`table-row-${row.name}`}
                        key={row.name}
                        className={`${row.required && "required"} ${
                          row.priority && "priority"
                        }`.trim()}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell component="th" scope="row">
                          {rowNameLabel(row)}
                        </TableCell>

                        <TableCell align="center">
                          <TemplateInput
                            data-testid="template-input"
                            row={row}
                            rowIndex={index}
                            handleInputChange={handleInputChange}
                            formulae={formulae}
                            saveFormula={saveFormula}
                          />
                        </TableCell>

                        {!isPoliticalData && (
                          <>
                            <TableCell
                              align="right"
                              style={{ minWidth: "190px" }}
                            >
                              {row.formula}
                              <ChooseFormulaModal
                                row={row}
                                rowIndex={index}
                                openLookupDirectly={false}
                                saveFormula={saveFormula}
                                formulae={formulae}
                              />
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    )
                )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

TemplateMapper.propTypes = {
  handleUpdatedTemplate: PropTypes.func,
};

export default TemplateMapper;
